import React from 'react';
import { Form, Button } from 'react-bootstrap';

/**
 * Reusable section for forms that have a repeatable list of rows (e.g. Additional Details, Supplier contacts).
 * Renders: hr, title (with optional headerAction aligned to title height), optional loading message, one row per item (content + remove button), add button.
 *
 * @param {string} title - Section heading (e.g. "Additional Details", "Supplier contacts")
 * @param {Array} items - Array of items to render
 * @param {function} getItemKey - (item, index) => string|number for React key
 * @param {function} onAdd - Called when add button is clicked
 * @param {function} onRemove - (index) => void when remove is clicked
 * @param {boolean} [loading] - When true and isEditMode, show loading message
 * @param {boolean} [isEditMode] - When true and loading, show "Loading..."
 * @param {string} [loadingMessage='Loading...'] - Text when loading
 * @param {string} [addButtonLabel] - Label for add button (e.g. "+ Add contact" or "+")
 * @param {string} [addButtonTitle] - title attribute for add button
 * @param {React.ReactNode} [headerAction] - Optional node rendered next to title (e.g. a button), aligned to title height
 * @param {boolean} [addButtonDisabled] - When true, disable the add button (e.g. global loading lock)
 * @param {boolean} [removeDisabled] - When true, disable each row's remove button
 * @param {function} children - Render prop (item, index) => React node (row content only; remove button is provided by this component)
 */
const RepeatableFormSection = ({
  title,
  items,
  getItemKey,
  onAdd,
  onRemove,
  loading = false,
  isEditMode = false,
  loadingMessage = 'Loading...',
  addButtonLabel = '+',
  addButtonTitle = 'Add',
  headerAction,
  addButtonDisabled = false,
  removeDisabled = false,
  children
}) => (
  <>
    <hr className="my-4 border-secondary" />
    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
      <h6 className="mb-0">{title}</h6>
      {headerAction != null ? headerAction : null}
    </div>
    {isEditMode && loading && (
      <Form.Text className="text-muted d-block mb-2">{loadingMessage}</Form.Text>
    )}
    {items.map((item, index) => (
      <div
        key={getItemKey(item, index)}
        className="d-flex gap-2 align-items-start mb-2 flex-wrap"
      >
        {children(item, index)}
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          className="px-2"
          title="Remove"
          disabled={removeDisabled}
          onClick={() => onRemove(index)}
          aria-label="Remove"
        >
          ×
        </Button>
      </div>
    ))}
    <Button
      type="button"
      variant="primary"
      className="mb-3"
      disabled={addButtonDisabled}
      onClick={onAdd}
      title={addButtonTitle}
      style={{ minWidth: '44px' }}
    >
      {addButtonLabel}
    </Button>
  </>
);

export default RepeatableFormSection;
